import { makeAutoObservable } from 'mobx';
import apolloClient from 'api/apolloClient';
import {
  ADD_PORTFOLIO_TO_CART,
  CREATE_CART,
  GET_CART,
  REMOVE_PORTFOLIO_FROM_CART,
  UPDATE_BC,
  REMOVE_BC_BY_INDEX,
  SAVE_PROPOSAL,
  COPY_CART,
  SAVE_QUOTE,
  UPDATE_RATE_SEGMENT,
} from 'api/cartService';
import { Cart, CartSource, PortfolioConfigRequest } from 'types/cartService';
import { ApolloQueryResult, FetchResult } from '@apollo/client';
import { CartBrowsingCriteria, RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';

export const INITIAL_BROWSING_CRITERIA = {
  effectiveDate: '',
  zipCode: '',
  state: '',
  county: '',
  shortName: {},
};

export const INITIAL_CART_OBJECT: Cart = Object.freeze({
  id: '',
  groupId: '',
  cartName: '',
  creationDateTime: '',
  cartItems: [],
  lastUpdateTime: '',
  rateSegment: 'GROUP',
  lastUpdateBrowsingCriteria: { ...INITIAL_BROWSING_CRITERIA },
});

export class CartStore {
  // eslint-disable-next-line class-methods-use-this
  async getCart(cartId: string): Promise<ApolloQueryResult<{ getCart: Cart }>> {
    return apolloClient.query<{ getCart: Cart }>({
      query: GET_CART,
      fetchPolicy: 'no-cache',
      variables: { id: cartId },
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async createCart(
    groupId: string,
    cartSource: CartSource,
    browsingCriteria: CartBrowsingCriteria,
    sponsorId: string,
    rateSegment?: RateSegment,
  ): Promise<FetchResult<{ createCart: Cart }>> {
    return apolloClient.mutate<{ createCart: Cart }>({
      mutation: CREATE_CART,
      fetchPolicy: 'no-cache',
      variables: { groupId, cartSource, browsingCriteria, sponsorId, rateSegment },
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async addPortfolioToCart(
    portfolioConfigRequest: PortfolioConfigRequest,
    sponsorId: string,
  ): Promise<FetchResult<{ addPortfolioConfigToCart: Cart }>> {
    return apolloClient.mutate<{ addPortfolioConfigToCart: Cart }>({
      mutation: ADD_PORTFOLIO_TO_CART,
      fetchPolicy: 'no-cache',
      variables: { portfolioConfigRequest, sponsorId },
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async removePortfolioFromCart(
    cartId: string,
    portfolioId: string,
    isLast: boolean,
  ): Promise<FetchResult<{ removePortfolioConfigFromCart: Cart }>> {
    return apolloClient.mutate<{ removePortfolioConfigFromCart: Cart }>({
      mutation: REMOVE_PORTFOLIO_FROM_CART,
      fetchPolicy: 'no-cache',
      variables: { cartId, portfolioId, removeLastActiveBC: isLast },
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async removeBcByIndex(
    cartId: string,
    portfolioId: string,
    index: number,
  ): Promise<FetchResult<{ removeBCByIndex: Cart }>> {
    return apolloClient.mutate<{ removeBCByIndex: Cart }>({
      mutation: REMOVE_BC_BY_INDEX,
      fetchPolicy: 'no-cache',
      variables: { cartId, portfolioId, index },
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async updateBC(
    cartId: string,
    browsingCriteria: CartBrowsingCriteria,
    sponsorId: string,
  ): Promise<FetchResult<{ updateLastBC: Cart }>> {
    return apolloClient.mutate<{ updateLastBC: Cart }>({
      mutation: UPDATE_BC,
      fetchPolicy: 'no-cache',
      variables: { cartId, browsingCriteria, sponsorId },
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async saveProposal(cartId: string, cartName: string): Promise<FetchResult<{ saveProposal: boolean }>> {
    return apolloClient.mutate<{ saveProposal: boolean }>({
      mutation: SAVE_PROPOSAL,
      fetchPolicy: 'no-cache',
      variables: { cartId, cartName },
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async copyCart(cartId: string): Promise<FetchResult<{ copyCart: Cart }>> {
    return apolloClient.mutate<{ copyCart: Cart }>({
      mutation: COPY_CART,
      variables: { id: cartId },
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async saveQuote(
    cartId: string,
    groupId: string,
    sponsorId: string,
    email: string,
    message: string,
    name: string,
  ): Promise<FetchResult<{ saveQuote: Cart }>> {
    return apolloClient.mutate<{ saveQuote: Cart }>({
      mutation: SAVE_QUOTE,
      variables: { cartId, email, groupId, sponsorId, message, name },
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async updateRateSegment(cartId: string, rateSegment: RateSegment): Promise<FetchResult<{ updateRateSegment: Cart }>> {
    return apolloClient.mutate<{ updateRateSegment: Cart }>({
      mutation: UPDATE_RATE_SEGMENT,
      variables: { cartId, rateSegment },
    });
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
